import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
  ColorsV2,
  Flex,
  getToken,
  InitialsInCircle,
  RouterLink,
  spaces,
  Text,
} from '@electricjs/arc';

import { getDisplayFullName } from 'common/utils/getFullName';

type EmployeeNameColumnProps = {
  firstName: string;
  preferredFirstName: string | undefined;
  lastName: string;
  email?: string | null;
  employeeId: string;
  linkToEmployeePage?: boolean;
  hideAvatar?: boolean;
  nameDecoration?: ReactNode;
  avatarVariant?: 'default' | 'brand';
  linkVariant?: 'default' | 'brand';
};

const StyledLink = styled(Link)`
  width: 100%;
  text-decoration: none;
  color: ${getToken(ColorsV2.TEXT)};

  &:hover {
    text-decoration: underline;
    text-decoration-color: ${getToken(ColorsV2.TEXT)};
  }

  &:active,
  visited {
    color: ${getToken(ColorsV2.TEXT)};
  }
`;

const ClippedText = styled(Text)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ClippedLinkText = styled(ClippedText)`
  color: inherit;
`;

export const BorderlessAvatarWrapper = styled.div`
  & > div {
    box-shadow: none !important;
  }
`;

const EmployeeNameColumn = ({
  firstName,
  preferredFirstName,
  lastName,
  email,
  employeeId,
  linkToEmployeePage = true,
  hideAvatar = false,
  nameDecoration,
  avatarVariant = 'default',
  linkVariant = 'default',
}: EmployeeNameColumnProps) => {
  const fullName = getDisplayFullName({
    firstName,
    preferredFirstName,
    lastName,
  });
  const isBrand = avatarVariant === 'brand';
  const isBrandLink = linkVariant === 'brand';

  return (
    <Flex vAlignContent="center" overflow="hidden">
      {!hideAvatar && (
        <Flex mr={isBrand ? spaces.space_m_rem : '4'} vertical>
          {isBrand ? (
            <BorderlessAvatarWrapper>
              <InitialsInCircle
                text={fullName || '?'}
                size="3.2rem"
                backgroundColor={ColorsV2.PRIMARY_LIGHTEST}
                textColor={ColorsV2.PRIMARY_DARK}
                fontSize="1.2rem"
              />
            </BorderlessAvatarWrapper>
          ) : (
            <InitialsInCircle text={fullName || '?'} />
          )}
        </Flex>
      )}
      <Flex vertical overflow="hidden">
        <Flex vAlignContent="center" gap={spaces.space_xs_rem}>
          {linkToEmployeePage ? (
            isBrandLink ? (
              <RouterLink to={`/employees/${employeeId}`} noUnderline>
                <ClippedLinkText variant="body-2" title={fullName}>
                  {fullName}
                </ClippedLinkText>
              </RouterLink>
            ) : (
              <StyledLink to={`/employees/${employeeId}`}>
                <ClippedText variant="body-2" title={fullName}>
                  {fullName}
                </ClippedText>
              </StyledLink>
            )
          ) : (
            <ClippedText variant="body-2" title={fullName}>
              {fullName}
            </ClippedText>
          )}
          {nameDecoration}
        </Flex>

        {!!email && (
          <Flex width="100%">
            <ClippedText variant="body-2" title={email}>
              {email}
            </ClippedText>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default EmployeeNameColumn;
